import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Wallet } from 'lucide-react';
import { authApi, persistAuthenticatedSession } from '../services/api';
import { EnvironmentBanner } from '../components/EnvironmentBanner';

interface PendingTwoFactorState {
    challengeId: string;
    username?: string;
}

const CODE_LENGTH = 6;

const readPendingChallenge = (state: unknown): PendingTwoFactorState | null => {
    if (state && typeof state === 'object' && 'challengeId' in state) {
        const candidate = state as PendingTwoFactorState;
        if (candidate.challengeId) return candidate;
    }

    try {
        const raw = sessionStorage.getItem('pending_two_factor');
        if (!raw) return null;
        const parsed = JSON.parse(raw) as PendingTwoFactorState;
        return parsed?.challengeId ? parsed : null;
    } catch {
        return null;
    }
};

const TwoFactorChallenge: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [logoSrc, setLogoSrc] = useState<string | null>('/logo.svg');
    const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

    const pendingChallenge = useMemo(
        () => readPendingChallenge(location.state),
        [location.state]
    );

    useEffect(() => {
        if (!pendingChallenge) {
            navigate('/login', { replace: true });
            return;
        }
        inputRefs.current[0]?.focus();
    }, [navigate, pendingChallenge]);

    const handleLogoError = () => {
        if (logoSrc === '/logo.svg') setLogoSrc('/logo.png');
        else setLogoSrc(null);
    };

    const updateDigit = (index: number, value: string) => {
        const nextChar = value.replace(/\D/g, '').slice(-1);
        setDigits((current) => {
            const next = [...current];
            next[index] = nextChar;
            return next;
        });

        if (nextChar && index < CODE_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Backspace' && !digits[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }

        if (event.key === 'ArrowLeft' && index > 0) {
            event.preventDefault();
            inputRefs.current[index - 1]?.focus();
        }

        if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
            event.preventDefault();
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault();
        const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
        if (!pasted) return;

        const nextDigits = Array(CODE_LENGTH).fill('').map((_, index) => pasted[index] || '');
        setDigits(nextDigits);

        const nextFocusIndex = Math.min(pasted.length, CODE_LENGTH - 1);
        inputRefs.current[nextFocusIndex]?.focus();
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!pendingChallenge) {
            navigate('/login', { replace: true });
            return;
        }

        const code = digits.join('');
        if (code.length !== CODE_LENGTH) {
            setError('Digite o codigo completo de 6 digitos.');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const response = await authApi.validateTwoFactor({
                challengeId: pendingChallenge.challengeId,
                token: code,
            });
            const { isAdmin } = persistAuthenticatedSession(response.data);
            try {
                sessionStorage.removeItem('pending_two_factor');
            } catch { }
            navigate(isAdmin ? '/admin' : '/', { replace: true });
        } catch (err: any) {
            const apiMsg = err?.response?.data?.message;
            const translatedMsg = err?.response?.data?.translatedMessage;
            setError(translatedMsg || apiMsg || 'Nao foi possivel validar o codigo informado.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-50 via-white to-secondary-100">
            <EnvironmentBanner />
            <div className="flex-1 flex items-center justify-center p-6">
                <div className="w-full max-w-lg bg-white rounded-[28px] shadow-xl border border-secondary-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-8 py-7 text-white">
                        <div className="flex items-center gap-3">
                            {logoSrc ? (
                                <img
                                    src={logoSrc}
                                    onError={handleLogoError}
                                    alt="My Finances Logo"
                                    className="h-11 w-11 rounded-xl bg-white/10 p-2 object-contain"
                                />
                            ) : (
                                <div className="h-11 w-11 rounded-xl bg-white/10 flex items-center justify-center">
                                    <Wallet className="h-6 w-6 text-white" />
                                </div>
                            )}
                            <div>
                                <h1 className="text-xl font-bold">Verificacao em duas etapas</h1>
                                <p className="text-sm text-primary-100">
                                    Digite o codigo de 6 digitos para concluir o acesso.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="p-8">
                        <div className="mb-6 flex items-start gap-4 rounded-2xl border border-primary-100 bg-primary-50 p-4">
                            <div className="rounded-2xl bg-white p-3 shadow-sm">
                                <ShieldCheck className="h-6 w-6 text-primary-600" />
                            </div>
                            <div>
                                <h2 className="text-base font-semibold text-secondary-900">Confirme sua identidade</h2>
                                <p className="mt-1 text-sm text-secondary-600">
                                    {pendingChallenge?.username
                                        ? `Informe o codigo gerado para o usuario ${pendingChallenge.username}.`
                                        : 'Informe o codigo gerado no seu aplicativo autenticador.'}
                                </p>
                            </div>
                        </div>

                        {error && (
                            <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-secondary-700 mb-3">
                                    Codigo de verificacao
                                </label>
                                <div className="grid grid-cols-6 gap-3">
                                    {digits.map((digit, index) => (
                                        <input
                                            key={index}
                                            ref={(element) => {
                                                inputRefs.current[index] = element;
                                            }}
                                            value={digit}
                                            onChange={(event) => updateDigit(index, event.target.value)}
                                            onKeyDown={(event) => handleKeyDown(index, event)}
                                            onPaste={handlePaste}
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                            maxLength={1}
                                            className="h-16 w-full rounded-2xl border border-secondary-200 bg-secondary-50 text-center text-2xl font-semibold tracking-[0.2em] text-secondary-900 outline-none transition-all focus:border-primary-400 focus:bg-white focus:ring-4 focus:ring-primary-100"
                                            aria-label={`Digito ${index + 1} do codigo`}
                                        />
                                    ))}
                                </div>
                                <p className="mt-3 text-xs text-secondary-500">
                                    Voce tambem pode colar o codigo completo.
                                </p>
                            </div>

                            <button
                                type="submit"
                                className="btn-primary w-full h-12 rounded-xl text-base"
                                disabled={submitting}
                            >
                                {submitting ? 'Validando...' : 'Confirmar acesso'}
                            </button>
                        </form>

                        <button
                            type="button"
                            onClick={() => {
                                try {
                                    sessionStorage.removeItem('pending_two_factor');
                                } catch { }
                                navigate('/login', { replace: true });
                            }}
                            className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-secondary-600 transition-colors hover:text-secondary-900"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Voltar para o login
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TwoFactorChallenge;
